import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Search, MapPin, Store, TrendingDown, ShieldCheck, Bell,
  Sparkles, ArrowRight, CheckCircle2, PiggyBank, Users, BarChart3,
} from "lucide-react";
import { api, euro } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1542838132-92c53300491e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMGdyb2NlcmllcyUyMG9yZ2FuaWN8ZW58MHx8fHwxNzg5MzY1MTM5fDA&ixlib=rb-4.1.0&q=85";
const SHOPPER = "https://images.unsplash.com/photo-1628102491629-778571d893a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxmcmVzaCUyMGdyb2NlcmllcyUyMG9yZ2FuaWN8ZW58MHx8fHwxNzg5MzY1MTM5fDA&ixlib=rb-4.1.0&q=85";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

export default function Landing() {
  const [community, setCommunity] = useState(null);
  useEffect(() => {
    api.get("/community/stats").then((r) => setCommunity(r.data)).catch(() => {});
  }, []);
  const nav = useNavigate();
  const findNearby = () => {
    if (!navigator.geolocation) { nav("/comparateur"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        localStorage.setItem("pmc_coords", JSON.stringify({ lat: pos.coords.latitude, lng: pos.coords.longitude }));
        nav("/comparateur");
      },
      () => nav("/comparateur"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };
  return (
    <div className="App">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Produits frais" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-secondary/80" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:py-32">
          <motion.div initial="hidden" animate="show" variants={fade} className="max-w-2xl">
            <Badge className="mb-5 rounded-full bg-white/10 text-white backdrop-blur border-white/20" data-testid="hero-badge">
              <Sparkles className="mr-1 h-3 w-3" /> Pilote Île-de-France
            </Badge>
            <h1 className="font-display text-4xl font-800 leading-[1.05] text-white sm:text-5xl lg:text-6xl">
              Le même panier,
              <br />
              <span className="text-primary">au prix le plus bas</span> près de chez vous.
            </h1>
            <p className="mt-6 max-w-xl text-base text-emerald-50/90 sm:text-lg">
              Composez votre liste de courses, on compare le coût total dans les
              supermarchés à proximité et on vous indique où faire le plus d'économies.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/comparateur">
                <Button size="lg" className="rounded-full text-base" data-testid="hero-start-btn">
                  Comparer mon panier <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/inscription">
                <Button size="lg" variant="outline" className="rounded-full border-white/30 bg-white/10 text-base text-white hover:bg-white hover:text-secondary" data-testid="hero-register-btn">
                  Créer un compte gratuit
                </Button>
              </Link>
              <Button size="lg" variant="outline" onClick={findNearby} className="rounded-full border-white/30 bg-white/10 text-base text-white hover:bg-white hover:text-secondary" data-testid="hero-geo-btn">
                <MapPin className="mr-2 h-4 w-4" /> Magasins près de moi
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Floating savings card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="pointer-events-none absolute bottom-8 right-6 hidden w-64 rounded-2xl border border-white/20 bg-white/95 p-5 shadow-2xl backdrop-blur lg:block"
          data-testid="hero-floating-card"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-secondary">
            <TrendingDown className="h-4 w-4 text-primary" /> Meilleure offre
          </div>
          <div className="mt-2 font-display text-3xl font-800 text-secondary">{euro(48.9)}</div>
          <p className="mt-1 text-xs text-muted-foreground">Panier de 24 articles · Lidl Montreuil</p>
          <div className="mt-3 flex items-center gap-1 text-sm font-semibold text-primary">
            <CheckCircle2 className="h-4 w-4" /> {euro(11.3)} économisés
          </div>
        </motion.div>
      </section>

      {/* COMMUNITY IMPACT */}
      <section className="border-b border-border bg-secondary" data-testid="community-widget">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
            className="flex flex-col items-center text-center">
            <Badge className="mb-4 rounded-full bg-primary/15 text-primary border-primary/20">
              <Sparkles className="mr-1 h-3 w-3" /> Impact communauté
            </Badge>
            <h2 className="font-display text-3xl font-800 tracking-tight text-white sm:text-4xl">
              Ensemble, nous avons déjà économisé
            </h2>
            <div className="mt-3 font-display text-5xl font-800 text-primary sm:text-6xl" data-testid="community-total-savings">
              {euro(community?.total_savings ?? 0)}
            </div>
            <p className="mt-2 max-w-lg text-sm text-emerald-50/70">
              Le cumul des économies réalisées par tous les utilisateurs de PanierMoinsCher.
            </p>
          </motion.div>

          <div className="mx-auto mt-10 grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-3">
            {[
              { icon: PiggyBank, label: "Économies cumulées", value: euro(community?.total_savings ?? 0), testid: "community-stat-savings" },
              { icon: Users, label: "Utilisateurs", value: (community?.users ?? 0).toLocaleString("fr-FR"), testid: "community-stat-users" },
              { icon: BarChart3, label: "Comparaisons", value: (community?.comparisons ?? 0).toLocaleString("fr-FR"), testid: "community-stat-comparisons" },
            ].map((s, i) => (
              <motion.div key={s.label} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
                className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center backdrop-blur" data-testid={s.testid}>
                <div className="mx-auto mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary">
                  <s.icon className="h-5 w-5" />
                </div>
                <div className="font-display text-2xl font-800 text-white">{s.value}</div>
                <p className="mt-1 text-xs text-emerald-50/60">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BENTO FEATURES */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <motion.h2 initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
          className="font-display text-3xl font-800 tracking-tight text-secondary sm:text-4xl">
          Optimisez tout le panier, pas seulement un prix
        </motion.h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Notre moteur tient compte des promotions, des tailles, des quantités et des
          produits équivalents pour vous donner le vrai coût de vos courses.
        </p>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            { icon: Search, title: "Recherche produits", desc: "Par nom, marque, catégorie ou code-barres. Ajoutez et ajustez les quantités.", span: "md:col-span-1" },
            { icon: Store, title: "Comparaison multi-magasins", desc: "Coût total, taux de couverture, promotions et articles manquants pour chaque enseigne.", span: "md:col-span-2" },
            { icon: MapPin, title: "Optimisation 2 magasins", desc: "Répartissez vos courses sur deux points de vente si les économies dépassent votre seuil.", span: "md:col-span-2" },
            { icon: Bell, title: "Alertes baisse de prix", desc: "Suivez vos produits favoris et vos paniers habituels.", span: "md:col-span-1" },
          ].map((f, i) => (
            <motion.div
              key={f.title}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fade}
              className={`group rounded-xl border border-border bg-card p-8 transition-transform duration-300 hover:-translate-y-1 hover:shadow-md ${f.span}`}
              data-testid={`feature-card-${i}`}
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-xl font-700 text-secondary">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-y border-border bg-muted/50">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
            <img src={SHOPPER} alt="Personne avec panier" className="w-full rounded-2xl object-cover shadow-lg" style={{ maxHeight: 460 }} />
          </motion.div>
          <div>
            <h2 className="font-display text-3xl font-800 tracking-tight text-secondary sm:text-4xl">
              Comment ça marche
            </h2>
            <ol className="mt-8 space-y-6">
              {[
                "Composez votre liste et indiquez votre code postal.",
                "On compare le total réel dans les magasins à proximité.",
                "On identifie le magasin le moins cher — ou la meilleure combinaison.",
                "Sauvegardez vos paniers et recevez des alertes de baisse de prix.",
              ].map((t, i) => (
                <li key={i} className="flex gap-4" data-testid={`how-step-${i}`}>
                  <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary font-display text-sm font-700 text-white">
                    {i + 1}
                  </span>
                  <p className="pt-1 text-secondary">{t}</p>
                </li>
              ))}
            </ol>
            <div className="mt-8 flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
              <ShieldCheck className="h-5 w-5 flex-none text-primary" />
              Prix datés et sources traçables. La géolocalisation est optionnelle et
              vous pouvez toujours saisir votre code postal.
            </div>
          </div>
        </div>
      </section>

      <footer className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="flex flex-col items-center justify-between gap-4 border-t border-border pt-8 text-sm text-muted-foreground sm:flex-row">
          <span className="flex items-center gap-2 font-display font-700 text-secondary">
            <img src="/logo192.png" alt="PanierMoinsCher" className="h-7 w-7 rounded-lg" />
            PanierMoinsCher
          </span>
          <span>Données démo + Open Food Facts · Pilote Île-de-France · {new Date().getFullYear()}</span>
        </div>
        <div className="mt-4 text-center text-xs text-muted-foreground sm:text-right">
          <Link to="/mentions-legales" className="hover:text-primary hover:underline" data-testid="footer-legal-link">Mentions légales</Link>
        </div>
      </footer>
    </div>
  );
}
